import React, { memo, ReactNode, useMemo } from 'react';

import * as C from './Charts.styled';
import { CHART_TONES, ChartTone } from './chartTokens';

export interface IRankBarItem {
  id: string;
  label: string;
  /** Значение столбика; `null` — прочерк, столбика нет. */
  value: number | null;
  /** Значение сравнения — тонкая отметка поверх дорожки. */
  compare?: number | null;
  /** Подпись значения: «46». */
  valueText: string;
  /** Дельта рядом со значением: «▲12». */
  deltaText?: string | null;
  deltaTone?: ChartTone;
  /** Вторая строка под подписью: «12 заявок / 480 посетителей». */
  secondary?: ReactNode;
  /** Мало данных — серым. */
  muted?: boolean;
  /** Цвет столбика (цвет канала). */
  color?: string;
  /** Выделенная строка (мы среди конкурентов). */
  highlight?: boolean;
}

export interface IRankBarRow extends IRankBarItem {
  /** Ширина столбика, % дорожки. */
  width: number;
  /** Положение отметки сравнения, % дорожки; `null` — нет. */
  compareAt: number | null;
}

/**
 * Ширины столбиков — от общего максимума текущего и сравнения: иначе
 * отметка «было больше» уезжала бы за край.
 */
export const rankBarsRows = (
  items: ReadonlyArray<IRankBarItem>,
): IRankBarRow[] => {
  const max = Math.max(
    0,
    ...items.flatMap((item) => [item.value ?? 0, item.compare ?? 0]),
  );
  const pct = (value: number | null | undefined) =>
    value === null || value === undefined || max <= 0
      ? 0
      : Math.max(0, Math.min(100, (value / max) * 100));
  return items.map((item) => ({
    ...item,
    width: pct(item.value),
    compareAt:
      item.compare === null || item.compare === undefined
        ? null
        : pct(item.compare),
  }));
};

export interface IRankBarsProps {
  items: ReadonlyArray<IRankBarItem>;
  /** Подпись списка для чтеца экрана. */
  ariaLabel: string;
  /** Клик по строке — инспектор строки. */
  onSelect?: (id: string) => void;
  /** Строка, чья панель открыта справа. */
  selectedId?: string | null;
  /** Подпись отметки сравнения для чтеца экрана: «было 41». */
  compareText?: (item: IRankBarItem) => string | null;
  /** Пусто. */
  emptyText?: string;
}

/**
 * ТЗ 0103 — горизонтальные полосы HTML (в духе `ExpenseBarList` Cash Flow):
 * длинные подписи целиком, строки кликаются и доступны с клавиатуры,
 * тонкая отметка — значение сравнения.
 */
export const RankBars = memo(
  ({
    items,
    ariaLabel,
    onSelect,
    selectedId = null,
    compareText,
    emptyText,
  }: IRankBarsProps) => {
    const rows = useMemo(() => rankBarsRows(items), [items]);
    if (!rows.length) {
      return emptyText ? <C.ChartEmpty>{emptyText}</C.ChartEmpty> : null;
    }
    return (
      <C.RankList aria-label={ariaLabel}>
        {rows.map((row) => {
          const was = compareText ? compareText(row) : null;
          const body = (
            <>
              <C.RankHead>
                <C.RankLabel title={row.label} $highlight={row.highlight}>
                  {row.label}
                </C.RankLabel>
                <C.RankValue $muted={row.muted}>
                  {row.valueText}
                  {row.deltaText && (
                    <C.RankDelta $tone={row.deltaTone ?? 'neutral'}>
                      {row.deltaText}
                    </C.RankDelta>
                  )}
                </C.RankValue>
              </C.RankHead>
              <C.RankTrack aria-hidden="true">
                <C.RankFill
                  $width={row.width}
                  $color={
                    row.muted ? CHART_TONES.neutral : row.color ?? undefined
                  }
                />
                {row.compareAt !== null && (
                  <C.RankCompare $at={row.compareAt} />
                )}
              </C.RankTrack>
              {row.secondary && (
                <C.RankSecondary>{row.secondary}</C.RankSecondary>
              )}
              {was && <C.SrOnly>{was}</C.SrOnly>}
            </>
          );
          return (
            <C.RankItem key={row.id}>
              {onSelect ? (
                <C.RankButton
                  type="button"
                  $selected={row.id === selectedId}
                  aria-pressed={row.id === selectedId}
                  onClick={() => onSelect(row.id)}
                >
                  {body}
                </C.RankButton>
              ) : (
                <C.RankStatic>{body}</C.RankStatic>
              )}
            </C.RankItem>
          );
        })}
      </C.RankList>
    );
  },
);

RankBars.displayName = 'RankBars';
